import { useRef, useSyncExternalStore } from "react";

/**
 * Read a slice of an external store (the trading view, a zustand store) that re-renders its reader
 * only when `isEqual` says the slice changed — zustand's `useStoreWithEqualityFn` for any
 * subscribe / getSnapshot pair. Market data and RiskState frames replace the store's state many
 * times a second; a reader that shows none of what changed keeps its previous slice and does not
 * render.
 *
 * While equal, the previous slice object is returned: a slice must carry only what `isEqual`
 * compares (select those fields, not the whole state), or its other fields go stale.
 */
export function useSlice<S, T>(
  subscribe: (onChange: () => void) => () => void,
  getSnapshot: () => S,
  select: (s: S) => T,
  isEqual: (a: T, b: T) => boolean,
): T {
  const memo = useRef<{ s: S; select: (s: S) => T; t: T } | null>(null);
  return useSyncExternalStore(subscribe, () => {
    const s = getSnapshot();
    const m = memo.current;
    if (m !== null && m.s === s && m.select === select) return m.t;
    const next = select(s);
    const t = m !== null && isEqual(m.t, next) ? m.t : next;
    memo.current = { s, select, t };
    return t;
  });
}

/** Same own keys, each `Object.is`-equal. */
export function shallowEqual<T extends object>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  const ka = Object.keys(a) as (keyof T)[];
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => Object.prototype.hasOwnProperty.call(b, k) && Object.is(a[k], b[k]));
}

/** Same length and pairwise `eq` (default `Object.is`). */
export function sameList<T>(
  a: readonly T[],
  b: readonly T[],
  eq: (x: T, y: T) => boolean = Object.is,
): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (!eq(a[i] as T, b[i] as T)) return false;
  return true;
}
